import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/scroll";
import { EASE } from "./Reveal";

const TYPES = ["General Inquiry", "Landscape Construction", "Landscape Design-Build", "Landscape Management"];

export const InquiryModal = ({ open, service, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && service) {
      const match = TYPES.find((t) => service.toUpperCase().includes(t.replace("Landscape ", "").toUpperCase().split("-")[0]));
      setForm((f) => ({ ...f, project_type: match || "General Inquiry" }));
    }
  }, [open, service]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Inquiry sent — our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
      onClose();
    } catch {
      toast.error("Something went wrong. Please call (209) 545-1696.");
    }
    setBusy(false);
  };

  const field = "w-full rounded-xl border border-charcoal/15 bg-white px-4 py-3 text-sm outline-none focus:border-flame transition-colors";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="inquiry-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-charcoal/55 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 34, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="relative w-full max-w-lg bg-cream rounded-[1.75rem] p-7 sm:p-10 shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <button
              data-testid="inquiry-modal-close-button"
              onClick={onClose}
              className="absolute top-5 right-5 w-9 h-9 rounded-full hover:bg-charcoal/5 flex items-center justify-center text-charcoal/60 transition-colors"
              aria-label="Close inquiry form"
            >
              <X className="w-4 h-4" />
            </button>
            <p className="font-mono text-[10px] tracking-[0.24em] text-ember mb-2">SEND INQUIRY</p>
            <h3 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-charcoal mb-7">
              Tell us about your landscape.
            </h3>
            <form onSubmit={submit} className="space-y-3.5">
              <input data-testid="inquiry-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name *" className={field} />
              <div className="grid sm:grid-cols-2 gap-3.5">
                <input data-testid="inquiry-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className={field} />
                <input data-testid="inquiry-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className={field} />
              </div>
              <select
                data-testid="inquiry-type-select"
                value={form.project_type}
                onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                className={`${field} text-charcoal/80`}
              >
                <option value="" disabled>Project type</option>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <textarea
                data-testid="inquiry-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us about the property and what you're planning…"
                rows={4}
                className={`${field} resize-none`}
              />
              <button
                data-testid="inquiry-submit-button"
                type="submit"
                disabled={busy}
                className="group relative w-full overflow-hidden rounded-xl text-white py-4 text-sm font-bold tracking-wide disabled:opacity-50 transition-transform duration-300 hover:scale-[1.01]"
              >
                <span className="texture-grain absolute inset-0 transition-transform duration-500 group-hover:scale-110" />
                <span className="relative z-10">{busy ? "SENDING…" : "SEND INQUIRY →"}</span>
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
